import { PageStatus } from '@tinct/core-api-models';
import {
  getPreviewBlockedReason,
  PreviewGateCampaign,
} from '../../hooks/use-preview-blocked-reason';

/** `reason` set ⇒ the action renders disabled with that text as tooltip. */
export type PageActionState = { visible: boolean; reason: string | null };

/**
 * `with-page`: the editor opens on this variant. `empty`: the editor opens
 * with no variant selected (the page has no content yet, the user picks
 * another one from the search).
 */
export type EditMode = 'with-page' | 'empty';

export type PageActionCapabilities = {
  edit: PageActionState & { mode: EditMode };
  copyUrl: PageActionState;
  exclude: PageActionState;
  include: PageActionState;
  publish: PageActionState;
  unpublish: PageActionState;
  preview: PageActionState;
};

/** Structural so both ReadPage (nullable status, staticUrl) and EmbeddedPage fit. */
export type CapabilityPage = {
  status: PageStatus | null;
  staticUrl?: string | null;
};

const HIDDEN: PageActionState = { visible: false, reason: null };
const ENABLED: PageActionState = { visible: true, reason: null };
const shown = (visible: boolean): PageActionState => (visible ? ENABLED : HIDDEN);

export function resolveEditMode(status: PageStatus | null): EditMode | 'disabled' {
  switch (status) {
    // A published variant is editable too: the funnel supports updating a live
    // campaign, and warns via the "changes aren't live yet" popup that the edit
    // only reaches visitors once the pages are regenerated and republished.
    case 'published':
    case 'ready':
    case 'error':
    case 'excluded':
    case 'unpublished':
      return 'with-page';
    case 'waiting':
    case 'queued':
    case 'generating':
      return 'empty';
    default:
      return 'disabled'; // excluded_conflict, draft, null
  }
}

function editBlockedReason(status: PageStatus | null): string | null {
  if (status === 'excluded_conflict') {
    return 'Resolve the audience conflict before editing this variant';
  }
  return 'This variant cannot be edited yet';
}

function copyUrlBlockedReason(page: CapabilityPage, campaign?: PreviewGateCampaign): string | null {
  if (page.status !== 'published') return 'Variant must be published to copy its URL';
  if (campaign?.status !== 'published') return 'Campaign must be live to copy variant URLs';
  if (campaign?.snippetStatus?.status !== 'active')
    return 'Tinct snippet must be installed on your website to copy variant URLs';
  if (!page.staticUrl) return 'No URL available for this variant';
  return null;
}

/**
 * Single source of truth for the variant actions menu (F-128 matrix), shared
 * by the campaign dashboard list, the variant dashboard header and the studio
 * page panel.
 *
 * Reads the STORED page status on purpose — not the effective (job-driven) one
 * — so it matches the backend guards: a published variant being regenerated is
 * still live, still unpublishable and still has a URL.
 *
 * Unpublish means "temporarily offline" (the next campaign publish brings the
 * variant back, and Publish puts it back on its own); Exclude means "never
 * publish". That is why an unpublished variant offers both Publish and Exclude
 * — without them, unpublishing is a dead end.
 */
export function getPageActionCapabilities(
  page: CapabilityPage,
  campaign?: PreviewGateCampaign,
): PageActionCapabilities {
  const status = page.status;
  const editMode = resolveEditMode(status);

  return {
    edit:
      editMode === 'disabled'
        ? { visible: true, reason: editBlockedReason(status), mode: 'with-page' }
        : { visible: true, reason: null, mode: editMode },
    copyUrl: { visible: true, reason: copyUrlBlockedReason(page, campaign) },
    exclude: shown(status === 'ready' || status === 'error' || status === 'unpublished'),
    include: shown(status === 'excluded'),
    publish: shown(status === 'unpublished'),
    unpublish: shown(status === 'published'),
    preview: { visible: true, reason: getPreviewBlockedReason({ status, campaign }) },
  };
}
